import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/LanguageContext";
import { LanguageSelector } from "../components/LanguageSelector";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, Ruler } from "lucide-react";
import { PasswordInput } from "../components/PasswordInput";

export default function Login() {
    const { setUser } = useAuth();
    const { t } = useI18n();
    const navigate = useNavigate();
    const [email, setEmail] = useState(() => new URLSearchParams(window.location.search).get("email") || "");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const { data } = await api.post("/auth/login", { email: email.trim(), password });
            setUser(data);
            navigate("/dashboard");
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    const googleLogin = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/dashboard";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <div className="min-h-screen flex bg-[#0b0f17] text-slate-100">
            <div className="hidden lg:flex flex-col justify-between w-1/2 p-12 cad-grid border-r border-[#1e293b] relative overflow-hidden">
                <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-600/30">
                        <Ruler className="w-6 h-6 text-white" />
                    </div>
                    <div className="font-mono font-bold text-lg tracking-tight">BLIND<span className="text-blue-500">CAD</span></div>
                </div>
                <div className="max-w-xl fade-in-up">
                    <h1 data-testid="login-brand-title" className="text-6xl xl:text-7xl font-mono font-extrabold tracking-tight leading-none">
                        BLIND<span className="text-blue-500">CAD</span>
                    </h1>
                    <p data-testid="login-brand-tagline" className="mt-6 text-slate-200 text-xl leading-relaxed max-w-lg">
                        {t("login_tagline")}
                    </p>
                    <div data-testid="login-brand-features" className="mt-8 flex gap-2 flex-wrap font-mono text-xs">
                        {t("login_features").split(" • ").map((f) => (
                            <span key={f} className="px-3 py-1.5 rounded-full bg-[#182232] border border-[#1e293b] text-slate-300">{f}</span>
                        ))}
                    </div>
                </div>
                <div className="text-xs text-slate-600 font-mono">CAD TACTICAL WORKSTATION · v1.0</div>
            </div>

            <div className="flex-1 flex items-center justify-center p-6">
                <div className="w-full max-w-sm fade-in-up">
                    <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
                        <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center">
                            <Ruler className="w-5 h-5 text-white" />
                        </div>
                        <div className="font-mono font-bold text-lg">BLIND<span className="text-blue-500">CAD</span></div>
                    </div>
                    <h2 className="text-2xl font-mono font-bold">{t("auth_login_title")}</h2>
                    <p className="text-slate-400 text-sm mt-1">{t("auth_login_sub")}</p>

                    <form onSubmit={submit} className="mt-8 space-y-4">
                        <div>
                            <Label htmlFor="email" className="text-slate-300">{t("auth_email")}</Label>
                            <Input id="email" name="email" autoComplete="username" data-testid="login-email-input" type="email" value={email}
                                onChange={(e) => setEmail(e.target.value)} required
                                className="mt-1.5 bg-[#111827] border-[#1e293b] text-slate-100" placeholder="name@company.com" />
                        </div>
                        <div>
                            <div className="flex items-center justify-between">
                                <Label htmlFor="password" className="text-slate-300">{t("auth_password")}</Label>
                                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-blue-400 hover:text-blue-300">{t("auth_forgot")}</Link>
                            </div>
                            <div className="mt-1.5">
                                <PasswordInput id="password" name="password" autoComplete="current-password" testId="login-password-input" value={password}
                                    onChange={(e) => setPassword(e.target.value)} required
                                    className="bg-[#111827] border-[#1e293b] text-slate-100" placeholder="••••••••" />
                            </div>
                        </div>
                        {error && <div data-testid="login-error" className="text-sm text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded-md px-3 py-2">{error}</div>}
                        <Button data-testid="login-submit-button" type="submit" disabled={loading}
                            className="w-full bg-blue-600 hover:bg-blue-500 text-white font-medium">
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("auth_login_btn")}
                        </Button>
                    </form>

                    <div className="my-6 flex items-center gap-3 text-slate-600 text-xs">
                        <div className="flex-1 h-px bg-[#1e293b]" /> {t("auth_or")} <div className="flex-1 h-px bg-[#1e293b]" />
                    </div>

                    <Button data-testid="login-google-button" onClick={googleLogin} variant="outline"
                        className="w-full bg-[#111827] border-[#1e293b] text-slate-100 hover:bg-[#182232]">
                        <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z"/></svg>
                        {t("auth_google")}
                    </Button>

                    <p className="mt-6 text-center text-sm text-slate-400">
                        {t("auth_no_account")} <Link to="/register" className="text-blue-400 hover:text-blue-300 font-medium">{t("auth_register_link")}</Link>
                    </p>
                    <div className="mt-6 flex justify-center"><LanguageSelector /></div>
                </div>
            </div>
        </div>
    );
}
